import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { api } from "../api";
import type { JournalEntry, User } from "../types";

interface JournalDraft {
  id: string;
  title: string;
  body: string;
  isPublic: boolean;
  tags: string[];
}

const emptyDraft: JournalDraft = { id: "", title: "", body: "", isPublic: false, tags: [] };

function cleanTag(value: string): string {
  return value.trim().replace(/^#+/, "").replace(/\s+/g, " ").slice(0, 32);
}

function normalizedTag(value: string): string {
  return cleanTag(value).toLocaleLowerCase("ru-RU");
}

function uniqueTags(entries: readonly JournalEntry[]): string[] {
  const tags = new Map<string, string>();
  for (const entry of entries) {
    for (const tag of entry.tags) tags.set(normalizedTag(tag), tag);
  }
  return [...tags.values()].sort((left, right) => left.localeCompare(right, "ru"));
}

function GearIcon() {
  return <span className="gear-icon" aria-hidden="true"><i /></span>;
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));
}

function deletionDate(value: string | null): string {
  const date = new Date(value ?? Date.now());
  date.setDate(date.getDate() + 30);
  return shortDate(date.toISOString());
}

function dateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromValue(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!, 12);
}

function DatePicker({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const initial = value ? dateFromValue(value) : new Date();
    return new Date(initial.getFullYear(), initial.getMonth(), 1);
  });
  const container = useRef<HTMLDivElement>(null);
  const today = new Date();
  const todayValue = dateValue(today);

  useEffect(() => {
    if (!open) return;
    function closeOnOutsideClick(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const calendarDays = useMemo(() => {
    const first = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const mondayOffset = (first.getDay() + 6) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - mondayOffset);
    return Array.from({ length: 42 }, (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index, 12));
  }, [visibleMonth]);

  function toggle() {
    if (!open) {
      const initial = value ? dateFromValue(value) : new Date();
      setVisibleMonth(new Date(initial.getFullYear(), initial.getMonth(), 1));
    }
    setOpen((current) => !current);
  }

  function choose(date: Date) {
    onChange(dateValue(date));
    setOpen(false);
  }

  return <div className="date-field" ref={container}>
    <span className="date-field-label">{label}</span>
    <button className={`date-picker-button ${open ? "active" : ""}`} type="button" onClick={toggle} aria-haspopup="dialog" aria-expanded={open}>
      <span className={value ? "" : "date-placeholder"}>{value ? new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(dateFromValue(value)) : "Выберите дату"}</span>
      <span className="calendar-icon" aria-hidden="true" />
    </button>
    {open && <div className="calendar-popover" role="dialog" aria-label={`Выбор даты: ${label}`}>
      <div className="calendar-head">
        <button type="button" onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))} aria-label="Предыдущий месяц">←</button>
        <strong>{new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" }).format(visibleMonth)}</strong>
        <button type="button" onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))} aria-label="Следующий месяц">→</button>
      </div>
      <div className="calendar-weekdays" aria-hidden="true">{["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((day) => <span key={day}>{day}</span>)}</div>
      <div className="calendar-days">
        {calendarDays.map((date) => {
          const key = dateValue(date);
          const outside = date.getMonth() !== visibleMonth.getMonth();
          return <button
            type="button"
            key={key}
            className={`${outside ? "outside" : ""} ${key === todayValue ? "today" : ""} ${key === value ? "selected" : ""}`}
            onClick={() => choose(date)}
            aria-label={new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(date)}
            aria-pressed={key === value}
          >{date.getDate()}</button>;
        })}
      </div>
      <div className="calendar-footer">
        <button type="button" onClick={() => { onChange(""); setOpen(false); }} disabled={!value}>Очистить</button>
        <button type="button" onClick={() => choose(today)}>Сегодня</button>
      </div>
    </div>}
  </div>;
}

function JournalWorkspace() {
  const [view, setView] = useState<"journal" | "trash">("journal");
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [trashEntries, setTrashEntries] = useState<JournalEntry[]>([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [trashSelection, setTrashSelection] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tagInputFocused, setTagInputFocused] = useState(false);

  async function loadJournal() {
    const result = await api.journal();
    setEntries(result.entries);
  }

  async function loadTrash() {
    const result = await api.trash();
    setTrashEntries(result.entries);
  }

  useEffect(() => {
    setLoading(true);
    const load = view === "journal" ? loadJournal() : loadTrash();
    void load.finally(() => setLoading(false));
  }, [view]);

  const selected = useMemo(() => entries.find((entry) => entry.id === draft.id), [entries, draft.id]);
  const visibleEntries = view === "journal" ? entries : trashEntries;
  const availableTags = useMemo(() => uniqueTags(visibleEntries), [visibleEntries]);
  const normalizedSearch = search.trim().toLocaleLowerCase("ru-RU");
  const normalizedSelectedTag = normalizedTag(selectedTag);
  const matchesFilters = (entry: JournalEntry) => (!normalizedSearch
    || entry.title.toLocaleLowerCase("ru-RU").includes(normalizedSearch)
    || entry.body.toLocaleLowerCase("ru-RU").includes(normalizedSearch))
    && (!normalizedSelectedTag || entry.tags.some((tag) => normalizedTag(tag) === normalizedSelectedTag));
  const filteredEntries = useMemo(() => entries.filter(matchesFilters), [entries, normalizedSearch, normalizedSelectedTag]);
  const filteredTrashEntries = useMemo(() => trashEntries.filter(matchesFilters), [trashEntries, normalizedSearch, normalizedSelectedTag]);
  const tagSuggestions = useMemo(() => availableTags.filter((tag) => {
    const normalized = normalizedTag(tag);
    return !draft.tags.some((current) => normalizedTag(current) === normalized)
      && (!normalizedTag(tagInput) || normalized.includes(normalizedTag(tagInput)));
  }).slice(0, 6), [availableTags, draft.tags, tagInput]);

  useEffect(() => {
    if (selectedTag && !availableTags.some((tag) => normalizedTag(tag) === normalizedTag(selectedTag))) {
      setSelectedTag("");
    }
  }, [availableTags, selectedTag]);

  function select(entry: JournalEntry) {
    setDraft({ id: entry.id, title: entry.title, body: entry.body, isPublic: entry.isPublic, tags: [...entry.tags] });
    setTagInput("");
    setMessage("");
  }

  function createNew() {
    setDraft({ ...emptyDraft, tags: [] });
    setTagInput("");
    setMessage("");
  }

  function changeVisibility(isPublic: boolean) {
    if (isPublic && !draft.isPublic && !window.confirm("Опубликовать эту запись на главной? Она станет доступна всем посетителям сайта.")) return;
    setDraft({ ...draft, isPublic });
  }

  function showJournal() {
    setView("journal");
    setTrashSelection(null);
    setMessage("");
    setSelectedTag("");
  }

  function showTrash() {
    setView("trash");
    setTrashSelection(null);
    setMessage("");
    setSelectedTag("");
  }

  function addTag(value = tagInput) {
    const tag = cleanTag(value);
    if (!tag || draft.tags.length >= 8 || draft.tags.some((current) => normalizedTag(current) === normalizedTag(tag))) {
      setTagInput("");
      return;
    }
    setDraft({ ...draft, tags: [...draft.tags, tag] });
    setTagInput("");
  }

  function handleTagKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addTag();
    }
    if (event.key === "Backspace" && !tagInput && draft.tags.length > 0) {
      setDraft({ ...draft, tags: draft.tags.slice(0, -1) });
    }
  }

  function removeTag(tag: string) {
    setDraft({ ...draft, tags: draft.tags.filter((current) => normalizedTag(current) !== normalizedTag(tag)) });
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const payload = { title: draft.title, body: draft.body, isPublic: draft.isPublic, tags: draft.tags };
      const result = draft.id ? await api.updateJournalEntry(draft.id, payload) : await api.createJournalEntry(payload);
      await loadJournal();
      select(result.entry);
      setMessage(result.entry.isPublic ? "Публичная запись сохранена." : "Личная запись сохранена.");
    } catch {
      setMessage("Не удалось сохранить запись.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!draft.id || !window.confirm("Переместить запись в корзину? Она будет храниться там 30 дней.")) return;
    setBusy(true);
    setMessage("");
    try {
      await api.deleteJournalEntry(draft.id);
      await Promise.all([loadJournal(), loadTrash()]);
      createNew();
      setMessage("Запись перемещена в корзину.");
    } catch {
      setMessage("Не удалось переместить запись в корзину.");
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    if (!trashSelection) return;
    setBusy(true);
    try {
      await api.restoreJournalEntry(trashSelection.id);
      await Promise.all([loadJournal(), loadTrash()]);
      setTrashSelection(null);
      setMessage("Запись восстановлена с исходной датой создания.");
    } catch {
      setMessage("Не удалось восстановить запись.");
    } finally {
      setBusy(false);
    }
  }

  async function removePermanently() {
    if (!trashSelection || !window.confirm("Удалить эту запись безвозвратно? Это действие нельзя отменить.")) return;
    setBusy(true);
    try {
      await api.permanentlyDeleteJournalEntry(trashSelection.id);
      await loadTrash();
      setTrashSelection(null);
      setMessage("Запись удалена безвозвратно.");
    } catch {
      setMessage("Не удалось удалить запись.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="journal-workspace">
      <aside className="entry-list">
        <div className="entry-list-head">
          <div><p className="site-kicker">{view === "journal" ? "ДНЕВНИК" : "30 ДНЕЙ"}</p><h1>{view === "journal" ? "Записи" : "Корзина"}</h1></div>
          {view === "journal"
            ? <button onClick={createNew} aria-label="Новая запись">＋</button>
            : <button className="back-to-journal" onClick={showJournal} aria-label="Вернуться к записям">←</button>}
        </div>
        <div className="entry-search">
          <span className="search-icon" aria-hidden="true" />
          <input aria-label={view === "journal" ? "Поиск по дневнику" : "Поиск по корзине"} placeholder={view === "journal" ? "Поиск по дневнику" : "Поиск по корзине"} value={search} onChange={(event) => setSearch(event.target.value)} />
          {search && <button type="button" onClick={() => setSearch("")} aria-label="Очистить поиск">×</button>}
        </div>
        {availableTags.length > 0 && <div className="entry-tag-filters" aria-label="Фильтр по тегам">
          {availableTags.map((tag) => <button type="button" className={normalizedTag(selectedTag) === normalizedTag(tag) ? "active" : ""} key={tag} onClick={() => setSelectedTag(normalizedTag(selectedTag) === normalizedTag(tag) ? "" : tag)}>#{tag}</button>)}
        </div>}
        <div className="entry-list-scroll">
          {loading && <p className="entry-placeholder">Загружаю…</p>}
          {!loading && view === "journal" && entries.length === 0 && <p className="entry-placeholder">Пока пусто.<br />Начните с первой записи.</p>}
          {!loading && view === "trash" && trashEntries.length === 0 && <p className="entry-placeholder">Корзина пуста.</p>}
          {!loading && (normalizedSearch || normalizedSelectedTag) && view === "journal" && entries.length > 0 && filteredEntries.length === 0 && <p className="entry-placeholder">Ничего не найдено.</p>}
          {!loading && (normalizedSearch || normalizedSelectedTag) && view === "trash" && trashEntries.length > 0 && filteredTrashEntries.length === 0 && <p className="entry-placeholder">Ничего не найдено.</p>}
          {view === "journal" && filteredEntries.map((entry) => <button className={`entry-list-item ${entry.id === draft.id ? "active" : ""}`} key={entry.id} onClick={() => select(entry)}>
            <span>{entry.isPublic ? "ПУБЛИЧНАЯ" : "ЛИЧНАЯ"}</span>
            <strong>{entry.title}</strong>
            {entry.tags.length > 0 && <div className="entry-item-tags">{entry.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>}
            <small>Создано {shortDate(entry.createdAt)} · изменено {shortDate(entry.updatedAt)}</small>
          </button>)}
          {view === "trash" && filteredTrashEntries.map((entry) => <button className={`entry-list-item ${entry.id === trashSelection?.id ? "active" : ""}`} key={entry.id} onClick={() => { setTrashSelection(entry); setMessage(""); }}>
            <span>ДО {deletionDate(entry.deletedAt)}</span>
            <strong>{entry.title}</strong>
            {entry.tags.length > 0 && <div className="entry-item-tags">{entry.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>}
            <small>Создано {shortDate(entry.createdAt)}</small>
          </button>)}
        </div>
        <button className={`trash-trigger ${view === "trash" ? "active" : ""}`} onClick={view === "trash" ? showJournal : showTrash} aria-label={view === "trash" ? "Вернуться к записям" : "Открыть корзину"}>
          <span className="trash-can-icon" aria-hidden="true" />
          {trashEntries.length > 0 && <b>{trashEntries.length}</b>}
        </button>
      </aside>

      {view === "journal" ? <form className="journal-editor" onSubmit={save}>
        <div className="editor-toolbar">
          <span>{selected ? `Создано ${shortDate(selected.createdAt)} · Изменено ${shortDate(selected.updatedAt)}` : "Новая запись"}</span>
          <div>{draft.id && <button className="delete-button" type="button" onClick={remove} disabled={busy}>В корзину</button>}<button className="save-button" disabled={busy}>{busy ? "Сохраняю…" : "Сохранить"}</button></div>
        </div>
        <input className="title-input" aria-label="Заголовок записи" placeholder="Название записи" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} maxLength={160} required />
        <textarea aria-label="Текст записи" placeholder="Начните писать…" value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} maxLength={100000} required />
        <div className="tag-editor">
          <div className="tag-editor-label"><span>Теги</span><small>{draft.tags.length}/8</small></div>
          <div className={`tag-input-shell ${tagInputFocused ? "focused" : ""}`}>
            {draft.tags.map((tag) => <button type="button" className="tag-chip removable" key={tag} onClick={() => removeTag(tag)} aria-label={`Удалить тег ${tag}`}><span>#</span>{tag}<b aria-hidden="true">×</b></button>)}
            {draft.tags.length < 8 && <input value={tagInput} onChange={(event) => setTagInput(event.target.value.slice(0, 32))} onKeyDown={handleTagKeyDown} onFocus={() => setTagInputFocused(true)} onBlur={() => window.setTimeout(() => setTagInputFocused(false), 120)} placeholder={draft.tags.length ? "Ещё тег" : "Добавить тег"} aria-label="Новый тег" />}
          </div>
          {tagInputFocused && tagSuggestions.length > 0 && <div className="tag-suggestions" aria-label="Ранее использованные теги">{tagSuggestions.map((tag) => <button type="button" key={tag} onPointerDown={(event) => event.preventDefault()} onClick={() => addTag(tag)}>#{tag}</button>)}</div>}
          <small className="tag-editor-hint">Enter или запятая добавляют тег.</small>
        </div>
        <div className="publish-row">
          <label className="publish-toggle"><input type="checkbox" checked={draft.isPublic} onChange={(event) => changeVisibility(event.target.checked)} /><span aria-hidden="true" /><div><strong>Опубликовать на главной</strong><small>По умолчанию запись видна только вам.</small></div></label>
          {message && <p role="status">{message}</p>}
        </div>
      </form> : <section className="trash-preview">
        {trashSelection ? <>
          <div className="trash-preview-toolbar"><span>Создано {shortDate(trashSelection.createdAt)}</span><span>Автоудаление {deletionDate(trashSelection.deletedAt)}</span></div>
          <h2>{trashSelection.title}</h2>
          <div className="trash-preview-body">{trashSelection.body.split(/\n{2,}/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
          <div className="trash-actions"><button className="restore-button" onClick={restore} disabled={busy}>Восстановить</button><button className="permanent-delete-button" onClick={removePermanently} disabled={busy}>Удалить безвозвратно</button></div>
        </> : <div className="trash-empty"><span className="trash-can-icon large" aria-hidden="true" /><h2>Корзина</h2><p>Удалённые записи хранятся здесь 30 дней. Выберите запись слева, чтобы восстановить её или удалить навсегда.</p>{message && <p className="trash-message">{message}</p>}</div>}
      </section>}
    </section>
  );
}

function SettingsWorkspace() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [visibility, setVisibility] = useState<"all" | "public" | "private">("all");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function exportJournal(event: FormEvent) {
    event.preventDefault();
    if (from && to && from > to) {
      setMessage("Начальная дата должна быть раньше конечной.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const { blob, filename } = await api.exportJournal({ from: from || undefined, to: to || undefined, visibility });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage("Word-файл сохранён на компьютер.");
    } catch {
      setMessage("Не удалось подготовить экспорт.");
    } finally {
      setBusy(false);
    }
  }

  return <section className="settings-workspace">
    <aside className="settings-tabs"><p className="site-kicker">НАСТРОЙКИ</p><h1>Настройки</h1><button className="active"><span>↓</span> Экспорт</button></aside>
    <div className="settings-panel">
      <p className="site-kicker">ЭКСПОРТ</p>
      <h2>Скачать дневник</h2>
      <p className="settings-intro">Записи будут собраны в один файл Word и сохранены на вашем компьютере. Записи из корзины в экспорт не входят.</p>
      <form className="export-form" onSubmit={exportJournal}>
        <fieldset><legend>Диапазон дат</legend><div className="date-fields"><DatePicker label="С даты" value={from} onChange={setFrom} /><DatePicker label="По дату" value={to} onChange={setTo} /></div><small>Оставьте даты пустыми, чтобы скачать весь дневник.</small></fieldset>
        <fieldset><legend>Какие записи</legend><label className="select-label"><select value={visibility} onChange={(event) => setVisibility(event.target.value as typeof visibility)}><option value="all">Все записи</option><option value="public">Только публичные</option><option value="private">Только личные</option></select></label></fieldset>
        <button className="export-button" disabled={busy}>{busy ? "Готовлю файл…" : "Скачать Word"}</button>
        {message && <p className="settings-message" role="status">{message}</p>}
      </form>
    </div>
  </section>;
}

export function Cabinet({ user, section, onNavigate, onLogout }: { user: User; section: "journal" | "settings"; onNavigate: (path: string) => void; onLogout: () => Promise<void> }) {
  return (
    <main className="cabinet-shell">
      <aside className="cabinet-sidebar">
        <button className="cabinet-brand" onClick={() => onNavigate("/")}>po<span>petr osetr</span></button>
        <nav>
          {user.isOwner && <button className={section === "journal" ? "active" : ""} onClick={() => onNavigate("/cabinet") }><span>⌁</span> Дневник</button>}
          <button disabled><span>＋</span> Скоро ещё</button>
          <button className={`mobile-settings-nav ${section === "settings" ? "active" : ""}`} onClick={() => onNavigate("/cabinet/settings")}><GearIcon /> Настройки</button>
        </nav>
        <div className="cabinet-user"><span>{user.displayName.slice(0, 1).toUpperCase()}</span><div><strong>{user.displayName}</strong><small>{user.email}</small></div><button className={`settings-button ${section === "settings" ? "active" : ""}`} onClick={() => onNavigate("/cabinet/settings")} aria-label="Настройки"><GearIcon /></button><button className="logout-button" onClick={onLogout}>Выйти</button></div>
      </aside>
      <section className="cabinet-content">
        {section === "settings" ? <SettingsWorkspace /> : section === "journal" && user.isOwner ? <JournalWorkspace /> : <p className="muted">Кабинет доступен только владельцу сайта.</p>}
      </section>
    </main>
  );
}
